<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { NodeViewWrapper } from '@tiptap/vue-3'
import EditableText from '@/components/offers/canvas/EditableText.vue'
import FigureSizer from '@/components/offers/canvas/FigureSizer.vue'
import { localSrc } from '@/components/offers/canvas/document.js'
import { widthItems } from '@/components/offers/canvas/figureWidth.js'

/**
 * A picture inside a text on the editor's page (D-150), as the document prints it: the image
 * at its width, "Figure N. caption" under it and the source in italics — the caption and the
 * source typed in place. N counts the titled pictures of the document before it.
 */
const props = defineProps({ node: { type: Object, required: true }, editor: { type: Object, required: true }, getPos: { type: Function, required: true }, updateAttributes: { type: Function, required: true }, selected: Boolean })
const figures = inject('docFigures', null)
const tick = ref(0)
const bump = () => tick.value++
props.editor.on('update', bump)
onBeforeUnmount(() => props.editor.off('update', bump))

const number = computed(() => {
  void tick.value
  if (!props.node.attrs.title) return null
  let n = 0
  const here = typeof props.getPos === 'function' ? props.getPos() : null
  props.editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.title && (here === null || pos < here)) n++
  })
  return (figures?.value?.start ?? 0) + n + 1
})
const labels = computed(() => figures?.value?.labels || { figure: 'Rysunek', source: 'Źródło' })
const editable = computed(() => props.editor.isEditable)
const typing = ref(false)
// Picked while its text is being edited (every text keeps a selection of its own).
const picked = computed(() => props.selected && props.editor.isFocused)
// Its width (D-207): dragged, chosen on its bar or in its menu under the right mouse button.
const doc = inject('docCanvas', null)
const { t } = useI18n()
const usable = computed(() => doc?.usable?.value || 170)
const setWidth = (mm) => props.updateAttributes({ width_mm: mm })
const menuKey = `image:${Math.random().toString(36).slice(2)}`
let unregister = null
onMounted(() => (unregister = doc?.registerMenu?.(menuKey, () => (editable.value ? [{ label: t('offers.imageWidth'), icon: 'fa-left-right', children: widthItems(t, props.node.attrs.width_mm, usable.value, setWidth) }] : []))))
onBeforeUnmount(() => unregister?.())
</script>

<template>
  <NodeViewWrapper as="figure" :class="['figure', selected ? 'doc-figure-selected' : '']" data-drag-handle @focusin="typing = true" @focusout="typing = false">
    <FigureSizer :src="localSrc(node.attrs.src)" :width-mm="node.attrs.width_mm" :max-mm="usable" :active="editable && picked" :data-image-menu="menuKey" @update="setWidth" />
    <p v-if="node.attrs.title || (editable && (picked || typing))" class="p-caption_figure" contenteditable="false">
      <span v-if="node.attrs.title && labels.figure">{{ labels.figure }} {{ number }}. </span>
      <EditableText :value="node.attrs.title || ''" :readonly="!editable" :placeholder="$t('offers.figureCaption')" @update="(v) => updateAttributes({ title: v || null })" />
    </p>
    <p v-if="node.attrs.source || (editable && (picked || typing))" class="p-source" contenteditable="false">
      <span v-if="node.attrs.source">{{ labels.source }}: </span>
      <EditableText :value="node.attrs.source || ''" :readonly="!editable" :placeholder="$t('offers.figureSource')" @update="(v) => updateAttributes({ source: v || null })" />
    </p>
  </NodeViewWrapper>
</template>
